import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { filter, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CustomFilterService } from '../../../../../core/services/custom-filter.service';
import { ELoadStatus } from '../../../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../../../utilities/http-utilities.service';
import { IWorkspace } from '../../../dashboards/dashboards.interface';
import { ICustomFilter } from '../../custom-filters/custom-filters.interface';
import {
  IUrlParamsTemplate,
  IUrlParamsTemplateAddPayload,
  IUrlParamsTemplateEditPayload,
} from '../endpoint-parameters.interface';

export interface IEndpointParametersAddEditDialogState {
  workspaces: IWorkspace[];
  workspacesLoading: ELoadStatus;
  customFilters: ICustomFilter[];
  customFiltersLoadStatus: ELoadStatus;
  createUpdateStatus: ELoadStatus;
}

@Injectable()
export class EndpointParametersAddEditDialogStore extends ComponentStore<IEndpointParametersAddEditDialogState> {
  private readonly httpUtilities = inject(HttpUtilitiesService);
  private readonly http = inject(HttpClient);
  private readonly customFilterService = inject(CustomFilterService);

  public readonly ELoadStatus = ELoadStatus;

  public readonly afterCreateUpdateSuccess$ = this.select(({ createUpdateStatus }) => createUpdateStatus).pipe(
    filter((status) => status === ELoadStatus.success),
  );

  public readonly workspaces: Signal<IWorkspace[]> = this.selectSignal(
    (state: IEndpointParametersAddEditDialogState) => state.workspaces,
  );

  public readonly workspacesLoading$: Observable<ELoadStatus> = this.select(
    (state: IEndpointParametersAddEditDialogState) => state.workspacesLoading,
  );

  public readonly customFilters: Signal<ICustomFilter[]> = this.selectSignal(
    (state: IEndpointParametersAddEditDialogState) => state.customFilters,
  );

  public readonly customFiltersLoaded$: Observable<boolean> = this.select(
    (state: IEndpointParametersAddEditDialogState) => state.customFiltersLoadStatus === ELoadStatus.success,
  );

  readonly loadWorkspaces = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({ workspacesLoading: ELoadStatus.loading });
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getWorkspaces(httpParams).pipe(
          tapResponse(
            (output: IGetManyResponse<IWorkspace>): void => {
              this.patchState({
                workspaces: output.data,
                workspacesLoading: ELoadStatus.success,
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading workspaces', error),
          ),
        );
      }),
    ),
  );

  readonly loadCustomFilters = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({ customFiltersLoadStatus: ELoadStatus.loading });
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.customFilterService.getMany({ params: httpParams }).pipe(
          tapResponse(
            (output): void => {
              this.patchState({
                customFilters: output.data,
                customFiltersLoadStatus: ELoadStatus.success,
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading workspaces', error),
          ),
        );
      }),
    ),
  );

  readonly createOne = this.effect((trigger$: Observable<IUrlParamsTemplateAddPayload>) =>
    trigger$.pipe(
      switchMap((item: IUrlParamsTemplateAddPayload) => {
        this.patchState({ createUpdateStatus: ELoadStatus.loading });

        return this.createItem(item).pipe(
          tapResponse(
            (): void => {
              this.patchState({ createUpdateStatus: ELoadStatus.success });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error creating template', error),
          ),
        );
      }),
    ),
  );

  readonly updateOne = this.effect((trigger$: Observable<IUrlParamsTemplateEditPayload>) =>
    trigger$.pipe(
      switchMap((item: IUrlParamsTemplateEditPayload) => {
        this.patchState({ createUpdateStatus: ELoadStatus.loading });

        return this.updateItem(item).pipe(
          tapResponse(
            () => this.patchState({ createUpdateStatus: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error updating template', error),
          ),
        );
      }),
    ),
  );

  constructor() {
    super({
      createUpdateStatus: ELoadStatus.initial,
      customFilters: [],
      customFiltersLoadStatus: ELoadStatus.initial,
      workspaces: [],
      workspacesLoading: ELoadStatus.initial,
    });
  }

  private getWorkspaces(params: HttpParams): Observable<IGetManyResponse<IWorkspace>> {
    return this.http.get<IGetManyResponse<IWorkspace>>('workspaces', { params });
  }

  private createItem(body: IUrlParamsTemplateAddPayload): Observable<IBaseResponse<IUrlParamsTemplate>> {
    return this.http.post<IBaseResponse<IUrlParamsTemplate>>('url-params-templates', body);
  }

  private updateItem(edit: IUrlParamsTemplateEditPayload): Observable<IBaseResponse<IUrlParamsTemplate>> {
    return this.http.patch<IBaseResponse<IUrlParamsTemplate>>(`url-params-templates/${edit.id}`, edit.dto);
  }
}
